// Display formatters + aging logic. The currency/date helpers are carried over
// from the scaffold's App.tsx (correct date-only parsing) per app.md §3/§8.

import {
  centsFromNumber,
  decimalsEqual,
  EASTERN_TIME_ZONE,
  formatMoney,
  lineTotalCents,
  normalizePrice,
  reconciles,
} from '@ap/shared'
import { DecisionStep } from './constants'
import type { LineItemTransportationDetail } from './types'

export { RECONCILE_TOLERANCE_CENTS } from '@ap/shared'

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

/**
 * NO DASHES, NO NULLS — ANYWHERE (owner override, 2026-08-19: "I was pretty
 * clear that we should never use dashes or nulls"). A previous pass kept the
 * em dash for a missing Subtotal or Total, reasoning a missing total is
 * "unknown, not zero" — the owner overruled that reasoning outright, for
 * every money value in the app, no exception. A null, undefined, empty or
 * unparseable input reads as $0.00. `MissingTotal` and the other flags say
 * "this figure was never extracted" — that is what the Flags panel is for,
 * not the number's own glyph.
 */
export function formatCurrency(value: number | string | undefined | null): string {
  if (value === undefined || value === null || value === '') return usd.format(0)
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return usd.format(0)
  return usd.format(n)
}

/** Money-field text at two places ("3918.6" -> "3918.60"). Blanks and
    non-numeric text pass through unchanged, so garbage reaches the server and is
    refused with the field named rather than read as zero. Whole-cent fields
    only: never a price (D-249) or a quantity (`quantityWire`). */
export function money2(v: string): string {
  if (v == null || v.trim() === '') return ''
  const n = Number(v)
  return Number.isNaN(n) ? v : n.toFixed(2)
}

// ---- Unit price: the one amount that is not whole cents (D-249) ------------
// The owner, 2026-09-30: keep the unit price exactly as printed, at whatever
// precision ("what if the next invoice charges us to the fifth decimal?"), and
// pay the Total to the penny. So a Price shows and edits every decimal it
// carries, and never passes through a two-place formatter; a line's Total is
// still whole cents, worked out by `lineTotal` below. Both read the shared
// rules (`normalizePrice`, `lineTotalCents`); what this file adds is the typed
// text a person leaves half-finished ("12.", ".5"), completed by `plainDecimal`.

/** A price's text, exact, never `toFixed`: the wire sends it as decimal text
    (`unit_price` is a `PriceString`), which `priceText` normalizes. A JS number
    — a mock seed, or a value a test builds — becomes `String(value)`, exact for
    every price the server accepts down to 1e-6; below that JavaScript prints an
    exponent, which `priceText` passes through and the server refuses. Null or
    blank is `''`. */
export function priceFromWire(value: number | string | undefined | null): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'number' && !Number.isFinite(value)) return ''
  return priceText(String(value))
}

/**
 * A QUANTITY AS THE WIRE TAKES IT (code review, 2026-10-03): a decimal string at
 * exactly four places, `numeric(14,4)`'s scale. Typed text is cleaned the way
 * `money2` used to clean it — thousands separators and spaces dropped, a
 * trailing point ("2.") read as the whole number — and a quantity of more than
 * four places is rounded ONCE, half away from zero, in decimal (never through a
 * float: 0.12345 becomes 0.1235). The server refuses more than four places, so
 * a typed quantity never reaches that refusal. A JS number goes through
 * `priceFromWire`'s exact decimal text first. Blank is null; text that is not a
 * number passes through unchanged, so the server refuses it with the field
 * named rather than the save clearing the quantity.
 */
export function quantityWire(value: number | string | null | undefined): string | null {
  if (value === undefined || value === null) return null
  const raw = typeof value === 'string' ? value.trim() : priceFromWire(value)
  if (raw === '') return null
  let t = raw.replace(/[,\s]/g, '')
  if (t.endsWith('.')) t = t.slice(0, -1)
  const m = /^(-?)(\d*)(?:\.(\d+))?$/.exec(t)
  if (!m || !/\d/.test(t)) return raw
  const [, sign = '', whole = '', fraction = ''] = m
  const places = 4
  const scaled = BigInt((whole || '0') + fraction.padEnd(places, '0').slice(0, places))
  const rest = fraction.length > places ? fraction.slice(places) : ''
  const roundUp = rest !== '' && rest[0]! >= '5'
  const magnitude = roundUp ? scaled + 1n : scaled
  const text = magnitude.toString().padStart(places + 1, '0')
  const negative = sign === '-' && magnitude !== 0n
  return `${negative ? '-' : ''}${text.slice(0, -places)}.${text.slice(-places)}`
}

/** A price's canonical text: at least two decimals, every decimal that carries
    value kept, trailing zeros past the second dropped — `"0.00490"` ->
    `"0.0049"`, `"12.5"` -> `"12.50"`. Blank or non-numeric text passes through
    unchanged (so garbage reaches the server and is refused with the field
    named), exactly as `money2` does for the whole-cent fields. */
export function priceText(v: string): string {
  if (v == null) return ''
  if (v.trim() === '') return ''
  const t = plainDecimal(v)
  return t === null ? v : normalizePrice(t)
}

/** Typed decimal text as a plain decimal the shared money rules read: trimmed,
    a half-typed leading or trailing point completed (`".5"` -> `"0.5"`,
    `"12."` -> `"12"`). `null` for a blank or anything that is not a decimal. */
function plainDecimal(v: string): string | null {
  if (v == null) return null
  const t = v.trim()
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(t)) return null
  return t.replace(/^(-?)\./, '$10.').replace(/\.$/, '')
}

/** Comma-grouped display text for a price — `"1234.0049"` -> `"1,234.0049"`,
    every decimal kept. The price twin of `groupedMoney`, which would round a
    price to two places. Non-numeric text passes through unchanged. */
export function groupedPrice(v: string): string {
  if (v == null || v.trim() === '') return v
  const t = priceText(v)
  if (!/^-?\d+\.\d+$/.test(t)) return v
  const negative = t.startsWith('-')
  const [whole = '0', fraction = ''] = (negative ? t.slice(1) : t).split('.')
  return `${negative ? '-' : ''}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${fraction}`
}

/** A read-only Price cell: `$` plus `groupedPrice`, so `$0.0049` shows as
    printed. A missing price reads `$0.00`, as `formatCurrency` reads one. */
export function formatPrice(value: number | string | undefined | null): string {
  const text = priceFromWire(value)
  if (text === '' || !/^-?\d+\.\d+$/.test(text)) return formatCurrency(0)
  const grouped = groupedPrice(text)
  return grouped.startsWith('-') ? `-$${grouped.slice(1)}` : `$${grouped}`
}

export function formatDate(value: string | undefined | null): string {
  if (!value) return '—'
  // A date-only value: parse the parts directly so the display never shifts a
  // day on a timezone conversion.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * The Eastern calendar day of a TIMESTAMP, written as `formatDate` writes a
 * date (D-451). `formatDate` reads a timestamp's leading `yyyy-MM-dd`, which is
 * the UTC day, so an evening upload would show the next day; every day
 * boundary here is Eastern (D-441).
 */
export function formatTimestampDate(value: string | undefined | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: EASTERN_TIME_ZONE })
}

/** MM/DD/YYYY — matches what the native date inputs display. */
export function formatNumericDate(value: string | undefined | null): string {
  if (!value) return '—'
  // A date-only value: parse the parts directly so the display never shifts a
  // day on a timezone conversion.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

export function formatDateTime(value: string | undefined | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function toNumber(value: number | string | undefined | null): number {
  if (value === undefined || value === null || value === '') return 0
  const n = typeof value === 'number' ? value : Number(value)
  // Not finite — NaN, or `Number('1e400')`, which is Infinity — is zero: a
  // money helper downstream (`centsFromNumber`) throws on a non-finite value.
  return Number.isFinite(n) ? n : 0
}

// ---- Money-field TEXT inputs (owner item 12, 2026-08-18) -------------------
// The comma-grouped boxes (Tax, Shipping, Total, Subtotal, line Price, line
// Total) trade `type="number"` for `type="text"` so a blurred field can show
// "1,234.56" — the HTML value-sanitization algorithm blanks a `type="number"`
// input the instant its value isn't a bare float, so a comma there reads as
// invalid and empties the box. Text accepts anything, which is what
// `filterMoneyInput` is for.
//
// THE ONE RULE: never let a comma into component state. These two functions
// only ever touch the RENDERED text — the caller decides, per keystroke and
// per focus state, what raw string actually gets stored (`EditableInvoiceForm`
// keeps the ungrouped string in state always, and swaps to the grouped one
// only in the `value` prop, only while the field is not focused). `toNumber`
// above returns a silent 0 on anything it can't parse, so one leaked comma in
// state would zero a line in `checkAmounts`, drop it from `derivedSubtotal`,
// and — via `decimalOrNull` — reach the server, which 400s on it.

const moneyGroup = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Comma-grouped display text for a money field's raw numeric string —
    "1234.56" -> "1,234.56". Blank or non-numeric text (a value still mid-edit,
    or simply empty) passes through unchanged, same as `formatCurrency` and
    `money2` already do: there's nothing valid to group yet. Deliberately not
    built on `formatCurrency` — these inputs already carry their own
    `contentBefore="$"`, and `formatCurrency` prepends a second one. */
export function groupedMoney(v: string): string {
  if (v == null || v.trim() === '') return v
  const n = Number(v)
  return Number.isNaN(n) ? v : moneyGroup.format(n)
}

/** What a money TEXT input may hold while the reviewer is typing: digits, at
    most one leading `-`, and at most one `.`. `type="number"` used to enforce
    this natively; `type="text"` accepts letters too, so the filtering the
    browser used to do for free now has to happen here. Applied on every
    keystroke, to the raw (ungrouped) string only — this never sees, and never
    produces, a comma. */
export function filterMoneyInput(raw: string): string {
  let s = raw.replace(/[^0-9.-]/g, '')
  s = s[0] === '-' ? '-' + s.slice(1).replace(/-/g, '') : s.replace(/-/g, '')
  const firstDot = s.indexOf('.')
  if (firstDot !== -1) s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '')
  return s
}

// ---- Invoice amount reconciliation -----------------------------------------
// One definition, used by the editor's release gate, its LineItemsToTotalMismatch
// Mark Resolved condition, the red amount borders, and the read-only footer pills
// at every later step. A COPY of `checkAmounts` in
// `backend/src/domain/amounts.ts`, kept because this screen holds money as JS
// numbers in dollars; the cents rule and the tolerance it applies are shared.
//
// ONE COMPARISON (owner, 2026-09-25 — `docs/decisions.md` D-151): the lines plus
// tax plus shipping against the printed Total, within three cents. It is the
// Point Click Care export guard's own test, so nothing the modal accepts can be
// refused at Export Control. Line items are PRE-TAX and PRE-SHIPPING, so both
// charges are added to them here. The SUBTOTAL is not an input: it is derived
// from the lines (D-3), a display figure rather than a third number to check.
//
// NULL POLICY, mirroring the backend: a null tax — and, identically, a null
// shipping — counts as ZERO, so an unrecorded charge cannot hide behind a total
// that silently includes it. A missing total never reconciles, and neither does
// a `null` lines sum (no line carries an amount): there is nothing to compare.
//
// THE TOLERANCE IS COMPARED IN WHOLE CENTS, not in dollars. This screen holds
// money as JS numbers, and `Math.abs(1000.07 - 1000.06) <= 0.01` is FALSE —
// the subtraction lands on 0.010000000000104592. Comparing the raw floats made
// the SPA disagree with the server on roughly one in six exactly-one-cent
// invoices around $1,000: the footer went red and Release went dark on an
// invoice the server would have accepted, and the reviewer had no move that
// would fix it. Rounding each side to cents first is the same exact-cents rule the
// backend applies with `RECONCILE_TOLERANCE_CENTS`, and it agrees with it exactly.
type AmountCheck = {
  /** `(lines + tax + shipping) - total`, in whole cents; `null` when the total
      is missing or no line carries an amount. */
  deltaCents: number | null
  /** Within three cents. What the release gate requires. */
  reconciles: boolean
}

/** Dollars to whole cents, half-away-from-zero — `centsFromNumber` from
    `@ap/shared`, exact from the number's decimal text. A null, blank or
    unparseable amount is zero, as `toNumber` reads it. */
function cents(value: number | string | undefined | null): number {
  return centsFromNumber(toNumber(value))
}

/** A difference of at most `RECONCILE_TOLERANCE_CENTS` (three cents), either
    way, reconciles — `reconciles` from `@ap/shared` (D-151). Integer cents in. */
export const reconcilesCents: (deltaCents: number) => boolean = reconciles

export function checkAmounts(
  /** Σ of the lines' totals, or `null` when no line carries an amount. */
  lineItemsSum: number | null,
  tax: number | string | undefined | null,
  total: number | string | undefined | null,
  /** The second header charge. Every call site that has a shipping figure MUST
      pass it: omitting it treats the charge as zero, which is exactly the hole
      the null policy above exists to close. */
  shipping?: number | string | null,
): AmountCheck {
  const totalKnown = total !== undefined && total !== null && total !== ''
  if (!totalKnown || lineItemsSum === null) return { deltaCents: null, reconciles: false }
  // A null tax or shipping counts as zero, matching the backend's NULL POLICY.
  const deltaCents = cents(lineItemsSum) + cents(tax) + cents(shipping) - cents(total)
  return { deltaCents, reconciles: reconcilesCents(deltaCents) }
}

/**
 * THE CHARGES HAVE A LINE TO LAND ON — the twin of `charges_have_a_line` in
 * `backend/src/domain/amounts.ts` (review of D-151, 2026-09-25). False only
 * when tax or shipping is non-zero and no line carries a non-zero amount: the
 * export spreads each charge over the lines in proportion to their amounts,
 * and over zero bases it can place nothing and refuses. The release gate
 * refuses the same case, so the export can never refuse what the modal
 * accepted.
 */
export function chargesHaveALine(
  lineTotals: readonly (number | string | null | undefined)[],
  tax: number | string | undefined | null,
  shipping: number | string | undefined | null,
): boolean {
  if (cents(tax) === 0 && cents(shipping) === 0) return true
  return lineTotals.some((t) => {
    if (t === null || t === undefined || (typeof t === 'string' && t.trim() === '')) return false
    const n = Number(t)
    return Number.isFinite(n) && cents(n) !== 0
  })
}

/** The lines' sum as `checkAmounts` wants it: Σ of the totals that carry an
    amount, or `null` when none does — the twin of `sumLineItemTotals`
    server-side, where an unknown amount is skipped rather than read as zero. */
export function sumKnownLineTotals(totals: readonly (number | string | null | undefined)[]): number | null {
  let known = false
  let sum = 0
  for (const t of totals) {
    if (t === null || t === undefined || (typeof t === 'string' && t.trim() === '')) continue
    const n = Number(t)
    if (!Number.isFinite(n)) continue
    sum += n
    known = true
  }
  return known ? sum : null
}

// ---- Derived amounts (docs/decisions.md D-3, D-4) --------------------------
// Two figures a person should never key. They live HERE rather than inside
// `EditableInvoiceForm.tsx`, which is their only caller, for the same reason
// `lib/readState.ts` and `lib/timelineLayout.ts` were pulled out of their
// components: a test can import a lib module without mounting React.

/**
 * A LINE'S TOTAL, DERIVED: `price x quantity`, rounded to the cent (owner,
 * 2026-08-14 — `docs/decisions.md` D-4). Locked in the editable form's table; this
 * is the only thing that computes it.
 *
 * A BLANK PRICE OR A BLANK QUANTITY gives an empty total rather than `0.00`:
 * there is genuinely no amount yet, and a printed `$0.00` would read as one.
 * The two blanks behave alike (QA C1-07, 2026-09-25 — `docs/decisions.md`
 * D-166). A blank quantity used to read as 1, so clearing Quantity left the
 * line's Total, the Subtotal and the green *Reconciles* exactly as they were,
 * while clearing Price emptied the Total and broke the sum. Both are required
 * at AP/AR and both now say so the same way: an empty Total, a Subtotal
 * without the line, and the reconcile red.
 *
 * Extraction's "a null quantity reads as 1" (D-10, `pass1LineItemTotalCents`) is
 * unchanged, and the extract worker now STORES that 1 (`quantity` is never
 * written null by extraction), so a line no reviewer has touched still opens
 * with the total extraction computed — the two rules still agree on every
 * value extraction can write.
 *
 * EXACT, AND ROUNDED ONCE (D-249, 2026-09-30): the price as it stands — every
 * decimal it carries, `0.0049` included — times the quantity, multiplied in
 * integers and rounded half-away-from-zero to the cent exactly once:
 * `lineTotalCents` from `@ap/shared`, the rule the server stores. The
 * editor shows the STORED Total and calls this only for a row edited this
 * session (code review D-02). So
 * `0.0049 x 6066` is `29.72`, a tiered price like `3 x 33.333` lands on
 * `100.00`, and `1.005 x 1` is `1.01` where a float product would give `1.00`.
 */
export function lineTotal(price: string, quantity: string): string {
  const p = plainDecimal(price)
  if (p === null) return ''
  const q = plainDecimal(quantity)
  if (q === null) return ''
  return formatMoney(lineTotalCents(p, q))
}

/** Two amounts' text are the same number, exactly, at any scale — `"2"`,
    `"2.0000"` and `"2.00"` are one quantity — `decimalsEqual` from
    `@ap/shared`. Blank equals blank; text that is not a decimal is compared as
    typed. */
export function sameDecimal(a: string, b: string): boolean {
  const blankA = a == null || a.trim() === ''
  const blankB = b == null || b.trim() === ''
  if (blankA || blankB) return blankA && blankB
  const x = plainDecimal(a)
  const y = plainDecimal(b)
  if (x === null || y === null) return a.trim() === b.trim()
  return decimalsEqual(x, y)
}

/**
 * THE INVOICE SUBTOTAL, DERIVED: the sum of the rows' own totals (D-3). Locked in
 * the editable form's header; this is the only thing that computes it.
 *
 * Empty — not `0.00` — when no row carries an amount, mirroring
 * `sumLineItemTotals` server-side: there is nothing priced to reconcile, and a
 * zero would read as though there were.
 */
export function derivedSubtotal(rows: readonly { total: string }[]): string {
  const priced = rows.filter((r) => r.total.trim() !== '' && !Number.isNaN(Number(r.total)))
  if (priced.length === 0) return ''
  const cents = priced.reduce((sum, r) => sum + Math.round(Number(r.total) * 100), 0)
  return (cents / 100).toFixed(2)
}

// ---- Aging (app.md §8) -----------------------------------------------------
// Age = now − invoice_date (invoice date). Color scale over 0–7 days:
// neutral 0–2, yellow 3–4, orange 5–6, red 7+. Always paired with the day count.
export type AgeBand = 'neutral' | 'yellow' | 'orange' | 'red'

export function ageInDays(invoiceDate: string | undefined | null, now: Date = new Date()): number | null {
  if (!invoiceDate) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(invoiceDate)
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(invoiceDate)
  if (Number.isNaN(d.getTime())) return null
  // Count calendar days between the two local dates. Diff the UTC-projected
  // y/m/d of each endpoint so a DST shift in the span can't drop or add a day.
  const startUtc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  const nowUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.max(0, Math.round((nowUtc - startUtc) / 86_400_000))
}

export function ageBand(days: number | null): AgeBand {
  if (days === null) return 'neutral'
  if (days >= 7) return 'red'
  if (days >= 5) return 'orange'
  if (days >= 3) return 'yellow'
  return 'neutral'
}

/**
 * "3 flags" / "1 flag" / "No flags" — the Import Control flags
 * pill's copy. Pluralized to match the age pill sitting beside it, which
 * already writes `{days} day{days === 1 ? '' : 's'}` (Badges.tsx).
 */
export function flagPillLabel(count: number): string {
  return count > 0 ? `${count} flag${count === 1 ? '' : 's'}` : 'No flags'
}

/**
 * "3 line items need action" / "1 line item needs action" — the Department /
 * Director / Oversight Approval coding-gate pill's copy. Structural twin of
 * `flagPillLabel` above, and here for the same two reasons: it is wording,
 * not rendering, and `app/mock/tests` has no component-render harness, so a
 * plain string function is the only part of a pill a test can call directly.
 *
 * OWNER, 2026-09-03. It read "N lines to finish" from 2026-08-17 until then.
 * Two things are better about the new phrasing and neither is decoration:
 *
 *  - **"line item", not "line".** The record has a name everywhere else in the
 *    app and in `docs/glossary.md`; the pill was the one place that clipped it.
 *  - **"need action", not "to finish".** "To finish" claims the line is
 *    incomplete, which is wrong for the case `docs/questions.md` records: a
 *    Capital Equipment line UNDER the floor is fully coded and still blocks
 *    the gate, so there is nothing left to finish and something still to do.
 *
 * NOT a revert to the wording rejected on 2026-08-17. That was
 * "N actions required", rejected for naming nothing visible and colliding with
 * the action buttons; this names the thing that needs the action, which is
 * exactly what that objection asked for. `uncoded-lines-pill.test.ts` still
 * guards the rejected phrase itself.
 *
 * No zero state, unlike `flagPillLabel`. "No flags" is a finding on a screen
 * whose subject IS flags; zero uncoded lines is just the precondition for
 * Approve already being enabled, so `UncodedLinesPill` renders nothing.
 */
export function uncodedLinesLabel(count: number): string {
  return count === 1 ? '1 line item needs action' : `${count} line items need action`
}

/**
 * How a line's GL coding reads where the cell is not editable — the approval
 * tiers, Closing Confirmation, Denial Review, the archives.
 *
 * Fully coded  → the code name.
 * Half coded   → the account plus an explicit "awaiting a Department".
 * Uncoded      → an em dash.
 *
 * THE MIDDLE CASE IS THE POINT. `code_name` is null while a line is half
 * coded, so rendering it alone made a line that IS coded — it just has no
 * department yet — look identical to one nobody has touched, at exactly the
 * steps whose job is to tell them apart.
 *
 * `accountLabel` is the caller's resolved label — `useHalfLabels().account`,
 * which every calling component already holds. Pass it: the fully coded case shows
 * `HSKG UNIFORMS` and the half-coded case beside it showed `67-506`, so the surface
 * that exists to make half-coded lines legible spoke in ids. It is optional only
 * so a caller with no master read still gets the id rather than a blank, which is
 * the same fallback the hook itself makes.
 *
 * "AWAITING A DEPARTMENT" (owner item 6, 2026-08-31). This was the last
 * user-facing string still spelling the old internal column name. The rest of
 * the app settled on **Department** for the leading half at owner item 8,
 * 2026-08-19 (`docs/app.md` §4 — "Account" is the trailing half, "Department"
 * the leading one), and `GLSummary.tsx`'s own pill for the same condition
 * already reads `$N awaiting a Department`. A reader who has just been shown a
 * column headed **Department** and a pill counting what is *awaiting a
 * Department* should not have to work out that a second name means that same
 * field. Same phrase, same field, same screen.
 */
export function lineItemCodingLabel(
  lineItem: {
    code_name?: string | null
    final_code_id?: string | null
    account_id?: string | null
  },
  accountLabel?: string,
): string {
  if (lineItem.code_name) return lineItem.code_name
  if (lineItem.final_code_id) return lineItem.final_code_id
  if (lineItem.account_id) {
    return `${accountLabel ?? lineItem.account_id} — awaiting a Department`
  }
  return '—'
}

/** "A — Newspaper ad": a Marketing category with its context, never the code
    alone — the whole reason `context` exists is that the code is meaningless
    without it. `'—'` when no category has been picked yet. Used identically by
    `EditableInvoiceForm.tsx` (the editable Import Control surface) and
    `InvoiceLineItemsTable.tsx`, inside each line's Additional Detail pop-up. */
export function detailCategoryLabel(
  categoryId: string | null | undefined,
  category: string | null | undefined,
  context: string | null | undefined,
): string {
  if (!categoryId) return '—'
  return `${category ?? categoryId}${context ? ` — ${context}` : ''}`
}

/**
 * Every screen `InvoiceLineItemsTable.tsx` can be mounted from — its one
 * caller's queue card (`InvoiceQueue.tsx`) and its one caller's read-only
 * modal branch (`InvoiceDetailModal.tsx`), across the whole Invoices tab.
 * `'archives'` names the Archives screen, which has no `DecisionStep` of its
 * own — a terminal invoice sits at no pending step.
 */
export type LineItemDetailScreen = DecisionStep | 'archives'

/**
 * A list set as prose, WITH THE OXFORD COMMA (owner, 2026-09-10: "complete
 * sentences with proper grammar, including the Oxford comma"). One item is
 * itself, two are joined by "and" alone, and three or more take the serial
 * comma before the final "and".
 *
 * `Array.prototype.join(', ')` is what this replaces: it produced
 * "4 added, 2 updated, 9 unchanged" inside a sentence, which reads as a tally
 * rather than as a clause.
 */
export function sentenceList(items: readonly string[]): string {
  if (items.length === 0) return ''
  if (items.length === 1) return items[0] as string
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

/**
 * The plural of a display noun, for a counted sentence.
 *
 * `${noun}s` is what this replaces, and it produced `2 companys marked
 * inactive.` on the References bulk strip (QA wave B1, B1-06). The nouns this
 * has to carry are the ones the app counts — vendor, company, code, account,
 * department, marketing category, vehicle, rule, guidance row — so the three
 * ordinary English rules cover all of them, applied to the LAST word of a
 * phrase ("marketing category" → "marketing categories"). It is not a general
 * inflector and does not try to be: an irregular noun added to that list gets
 * its own entry here, in front of the rules, rather than a second helper.
 */
export function pluralize(noun: string): string {
  const cut = noun.lastIndexOf(' ')
  const head = cut === -1 ? '' : noun.slice(0, cut + 1)
  const word = cut === -1 ? noun : noun.slice(cut + 1)
  // "guidance" has no plural; a count of it is entries (QA 2026-10-04 A-06).
  const irregular: Record<string, string> = { person: 'people', status: 'statuses', guidance: 'guidance entries' }
  const known = irregular[word.toLowerCase()]
  if (known) return head + known
  // Consonant + y: company → companies. A vowel before the y keeps it (day →
  // days), which is why the character before it is tested rather than the y.
  if (/[^aeiou]y$/i.test(word)) return `${head}${word.slice(0, -1)}ies`
  // A sibilant ending needs the extra syllable: box → boxes, batch → batches.
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${head}${word}es`
  return `${head}${word}s`
}

/** `1 vendor` / `2 vendors` — the count and its noun, agreeing. */
export function countOf(n: number, noun: string): string {
  return `${n} ${n === 1 ? noun : pluralize(noun)}`
}

/** A heading whose noun agrees with its count, the count shown elsewhere:
    `Vendor Added`, `Vendors Added` (the import previews, owner 2026-10-20). */
export function countedHeading(n: number, noun: string, verb: string): string {
  return `${n === 1 ? noun : pluralize(noun)} ${verb}`
}

/**
 * `a vendor` / `an account` — a display noun behind the right article.
 *
 * `A ${noun}` is what this replaces, and on References → Codes → Accounts it
 * produced `A account with this ID already exists.` (QA wave B1, B1-09). The
 * test is the noun's first LETTER, which is right for every noun this app puts
 * in front of an article; it is not a pronunciation rule and does not handle
 * "an hour" or "a union".
 */
export function withArticle(noun: string): string {
  return `${/^[aeiou]/i.test(noun) ? 'an' : 'a'} ${noun}`
}

/** First letter up, the rest untouched — for a sentence that starts with a
    value, such as the article `withArticle` returns. */
export function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * THE TOAST AFTER A SAVE ON REFERENCES AND RULES — one vocabulary for every
 * tab (QA wave B, 2026-09-25, B-05; `docs/decisions.md` D-170).
 *
 *   * a new record: *The vendor was added.*
 *   * a save that moved Status: *The code is now Active.* / *… is now
 *     Inactive.* — the Status change is the headline, whatever else moved;
 *   * any other save: *The rule was saved.*
 *   * a save that changed nothing: `NOTHING_CHANGED`, and nothing is written.
 *
 * The siblings had drifted: a Code made Inactive said *is now Inactive* and
 * made Active again said *was updated*; four Rules tabs said *was updated*
 * both ways, Guidance *was saved*. "Saved" is the one verb for an edit now,
 * and `app/mock/tests/rules-references-round-trips.test.ts` fails on a literal
 * *was updated* toast in any References or Rules source.
 */
export function savedToast(
  thing: string,
  change: { wasNew?: boolean; activeBefore?: boolean; activeAfter?: boolean } = {},
): string {
  if (change.wasNew) return `The ${thing} was added.`
  if (
    change.activeBefore !== undefined &&
    change.activeAfter !== undefined &&
    change.activeBefore !== change.activeAfter
  ) {
    return statusToast(thing, change.activeAfter)
  }
  return `The ${thing} was saved.`
}

/**
 * THE TOAST FOR A SAVE THAT CHANGED NOTHING, on every References and Rules
 * editor (D-170; QA wave B review, B-02 and B-05). The editor closes WITHOUT
 * writing: a PATCH carrying only `row_version` is a no-op the server has to
 * answer specially, and an editor that sends every field would write the row
 * (and bump its version) for nothing.
 */
export const NOTHING_CHANGED = 'Nothing was changed.'

/** *The <thing> is now Active.* / *… is now Inactive.* — see `savedToast`. */
export function statusToast(thing: string, active: boolean): string {
  return `The ${thing} is now ${active ? 'Active' : 'Inactive'}.`
}

/**
 * End a sentence with ONE full stop (QA wave B, B-10). A rule's sentence
 * often ends on a name, and a name can end in its own period —
 * `PACE Healthcare, Inc.` read *"… routing goes to PACE Healthcare, Inc.."*.
 *
 * A closing curly quote after the stop counts as the end too (QA 2026-10-01,
 * D1-03): a hold's summary ends on its quoted reason, and a reason that ends
 * in a full stop read *"… a corrected copy.”."*.
 */
export function closeSentence(text: string): string {
  return /[.!?]”?$/.test(text) ? text : `${text}.`
}

/**
 * The line under a vendor's name in the invoice modal's Vendor picker (owner,
 * 2026-09-29, D-208: "please add the Vendor ID before the address subtext").
 * The ID first, then the city and state, joined by the ` · ` the app's other
 * picker subtexts use: `6051 · Bridgewater, NJ`. A missing part is dropped,
 * never shown as an empty slot.
 *
 * THE REMITTANCE ADDRESS IS THE THIRD PART (owner, 2026-09-30, D-241), in the same
 * city-and-state form: `1006 · Johnstown, PA · PITTSBURGH, PA`. A vendor with no
 * remittance city or state shows the first two parts only.
 */
export function vendorOptionSubtext(v: {
  vendor_id?: string | null
  city?: string | null
  state_or_province?: string | null
  remittance_city?: string | null
  remittance_state_or_province?: string | null
}): string {
  return [v.vendor_id, vendorPlace(v), vendorRemittancePlace(v)].filter(Boolean).join(' · ')
}

/** A vendor's primary city and state, `Bridgewater, NJ`; '' when it has neither. */
export function vendorPlace(v: { city?: string | null; state_or_province?: string | null }): string {
  return [v.city, v.state_or_province].filter(Boolean).join(', ')
}

/** The Remittance Address's city and state (D-241), in `vendorPlace`'s form. */
export function vendorRemittancePlace(v: {
  remittance_city?: string | null
  remittance_state_or_province?: string | null
}): string {
  return [v.remittance_city, v.remittance_state_or_province].filter(Boolean).join(', ')
}

/**
 * HISTORY'S ESCALATED ROW (`./history.ts`; Decision History's until D-329).
 * THE AUTOMATIC SKIP READS AS TWO COMPLETE SENTENCES (owner, 2026-09-29,
 * D-235): "Manager Approval was skipped due to inactivity on September 29,
 * 2026 at 8:46 PM. The invoice spent three days at Manager Approval without a
 * decision." It first rendered in the person-decision pattern, "Manager
 * Approval — Skipped due to inactivity on <date>: “<reason>”" (D-214), which
 * is neither a title nor a sentence. No actor: `system:escalation` is not
 * someone who decided. DISPLAY ONLY — the stored outcome is still
 * `escalated`. The reason is the one the worker stored (the worker is gone
 * since D-336; its rows stay); a row written before D-235 keeps its older text, and a reason without closing punctuation
 * gets a full stop so the line still ends as a sentence.
 */
export function escalatedLine(step: string, when: string, reason: string | null | undefined): string {
  const first = `${step} was skipped due to inactivity${when ? ` on ${when}` : ''}.`
  const second = (reason ?? '').trim()
  if (!second) return first
  return `${first} ${/[.!?]$/.test(second) ? second : `${second}.`}`
}

/**
 * The Codes editor's warning when "that includes" normalizes to nothing. With a
 * vendor, the rule would cover every line from it; with Any vendor there is no
 * "this vendor" to name, and the rule cannot be saved at all (QA 2026-10-04
 * A-03; docs/app.md §6 Rules, Codes).
 */
export function emptiedScopeWarning(anyVendor: boolean): string {
  const how = 'Line descriptions are matched with digits removed and punctuation collapsed.'
  return anyVendor
    ? `That text normalizes to nothing, so there is nothing to match on, and a rule for any vendor needs some. ${how}`
    : `That text normalizes to nothing, so the rule would apply to every line item from this vendor. ${how}`
}

/**
 * Guidance → Add Rule, for a vendor that already has Active guidance: say
 * plainly that saving replaces it (docs/app.md §6 Rules, Guidance; QA
 * 2026-10-04 A-04). The current text is shown beside this, read-only.
 */
export function guidanceReplacesNotice(vendorName: string): string {
  const who = vendorName.trim() === '' ? 'This vendor' : vendorName.trim()
  return `${who} already has guidance, shown below. Saving replaces it, and the current guidance is kept as Inactive.`
}

/**
 * "marketing category" — the one spelling every toast on the category list
 * uses. The save said "The Marketing category
 * was added." and the bulk strip "The marketing category is now Inactive." (QA
 * 2026-10-04 B-06); lower case, like every other noun in a toast.
 */
export function categoryNoun(kind: string): string {
  return `${kind.toLowerCase()} category`
}


/** A blank text value, as the app shows one elsewhere. */
const EMPTY_TEXT = '—'

/** One row of Fleet Maintenance's detail as the pop-up shows it. */
interface TransportationDetailRow {
  label: string
  value: string
}

/**
 * Fleet Maintenance's detail (D-381), label then value, in the order the
 * pop-up lists it. Money reads as every other amount does (`formatCurrency`);
 * a blank text value is the dash.
 */
export function transportationDetailRows(detail: LineItemTransportationDetail): TransportationDetailRow[] {
  const text = (v: string | null) => (v == null || v.trim() === '' ? EMPTY_TEXT : v)
  return [
    { label: 'Category', value: text(detail.category) },
    { label: 'Action', value: text(detail.action) },
    { label: 'Parts Cost', value: formatCurrency(detail.parts_cost) },
    { label: 'Labor Cost', value: formatCurrency(detail.labor_cost) },
    { label: 'Labor Hours', value: text(detail.labor_hours) },
    { label: 'Notes', value: text(detail.notes) },
  ]
}

/** What a group row's Fleet Maintenance field reads (owner, 2026-10-28 — D-425). */
const SEE_SPECIFIC_LINE_ITEMS = 'See specific line items.'

/**
 * A GROUP'S FLEET MAINTENANCE DETAIL (owner, 2026-10-28 — D-425: "Fleet
 * Maintenance detail can vary between line items within a group, so in the
 * Additional Detail box at the group level, show those fields when applicable
 * with 'See specific line items.'"): the six labels, each reading that.
 */
export function transportationRollUpRows(): TransportationDetailRow[] {
  const blank = { category: null, action: null, parts_cost: null, labor_cost: null, labor_hours: null, notes: null, row_version: 0 }
  return transportationDetailRows(blank).map((r) => ({ label: r.label, value: SEE_SPECIFIC_LINE_ITEMS }))
}

/**
 * THE HEADER TAX OR SHIPPING FIELD SHOWS ONLY WHERE IT HOLDS SOMETHING (owner,
 * 2026-10-27 — D-420: "I want to remove Tax and Shipping from the invoice
 * level"). Tax and shipping are lines in the invoice's Tax and Shipping groups
 * now; an invoice from before the change may still store a non-zero header
 * charge, and there the field shows as it did, so that invoice reconciles and
 * can be corrected. Read from the STORED invoice, never the box being typed in.
 */
export function showsHeaderCharge(stored: number | string | null | undefined): boolean {
  return cents(stored) !== 0
}
